import cube, { CubeApi, type Query } from "@cubejs-client/core";
import type { ApiError } from "@/shared/types";

/**
 * CubeJS service adapter (convention section 33).
 * Responsibilities: client configuration, query execution, error
 * normalization, auth integration. It must not know which widget uses it.
 */

const apiUrl = import.meta.env.VITE_CUBE_API_URL;
const useMock = import.meta.env.VITE_CUBE_MOCK === "true" || !apiUrl;

/** Whether the service is running without a backend (deterministic mocks). */
export function isCubeMock(): boolean {
  return useMock;
}

let client: CubeApi | null = null;

function getClient(): CubeApi {
  if (!client) {
    // Auth integration point: replace the static token with a token provider.
    const token = import.meta.env.VITE_CUBE_API_TOKEN ?? "";
    client = cube(token, { apiUrl: apiUrl as string });
  }
  return client;
}

/** Normalized, widget-facing query result: plain rows, no CubeJS SDK types. */
export interface CubeQueryResult {
  rows: Array<Record<string, string | number>>;
}

/** Query shape accepted from widgets (subset of CubeJS `Query`). */
export type CubeQueryInput = Query;

export function normalizeApiError(error: unknown): ApiError {
  if (error && typeof error === "object" && "code" in error && "message" in error) {
    return error as ApiError;
  }
  const message =
    error instanceof Error ? error.message : "Unknown CubeJS error";
  return { code: "CUBE_ERROR", message, cause: error };
}

/** Deterministic mock data so the app demos without a backend. */
function mockExecute(query: CubeQueryInput): Promise<CubeQueryResult> {
  const dimensions = (query.dimensions ?? []) as string[];
  const measures = (query.measures ?? []) as string[];
  const limit = typeof query.limit === "number" ? query.limit : 30;
  const rows: Array<Record<string, string | number>> = [];

  for (let i = 0; i < limit; i++) {
    const row: Record<string, string | number> = {};
    for (const dimension of dimensions) {
      const label = dimension.split(".").pop() ?? dimension;
      row[dimension] = `${label} ${i + 1}`;
    }
    for (const measure of measures) {
      // Deterministic pseudo-random values per measure/row.
      const seed = (measure.length * 31 + i * 17) % 100;
      row[measure] = (seed + 1) * 3.7;
    }
    rows.push(row);
  }
  // Simulated latency keeps loading states visible.
  return new Promise((resolve) => setTimeout(() => resolve({ rows }), 350));
}

/**
 * Execute a CubeJS query and normalize the response.
 * Supports cancellation via AbortSignal (best effort; the SDK client is
 * recreated per request when a signal is present).
 */
export async function executeCubeQuery(
  query: CubeQueryInput,
  signal?: AbortSignal,
): Promise<CubeQueryResult> {
  try {
    if (useMock) {
      return await mockExecute(query);
    }

    const resultSet = signal
      ? await getClient().load(query, { signal } as never)
      : await getClient().load(query);

    const raw = resultSet.rawData() as Array<Record<string, string | number>>;
    return { rows: raw };
  } catch (error) {
    // Aborts are not errors for callers.
    if (signal?.aborted) {
      throw { code: "ABORTED", message: "Request aborted" } satisfies ApiError;
    }
    throw normalizeApiError(error);
  }
}
